import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

const NotificationBell = ({ currentUser }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Detekce změny velikosti okna pro responsivitu
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Načíst notifikace při mountu a každých 30 sekund
  useEffect(() => {
    if (currentUser?._id) {
      fetchNotifications();
      
      const interval = setInterval(() => {
        fetchNotifications();
      }, 30000); // Každých 30 sekund

      return () => clearInterval(interval);
    }
  }, [currentUser]);

  // Zavřít dropdown při kliknutí mimo něj (pouze na desktopu)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isMobile && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobile]);

  const fetchNotifications = async () => {
    if (!currentUser?._id) return;

    try {
      const response = await fetch(`${API_URL}/notifications?userId=${currentUser._id}`);
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        setUnreadCount(data.length);
      }
    } catch (error) {
      console.error('Chyba při načítání notifikací:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const response = await fetch(`${API_URL}/notifications/${notificationId}/read`, {
        method: 'POST',
      });

      if (response.ok) {
        // Odstraň notifikaci ze seznamu
        setNotifications(prev => prev.filter(n => n._id !== notificationId));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Chyba při označování notifikace jako přečtené:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Označ jako přečtené
    await markAsRead(notification._id);
    
    // Zavři dropdown
    setIsOpen(false);
    
    // Pro lead_assignment přejdi na detail firmy
    if (notification.type === 'lead_assignment' && notification.companyId) {
      navigate(`/companies/${notification.companyId}`);
      return;
    }

    // Přesměruj na Requests s filtrem podle IČO
    navigate('/requests', { 
      state: { 
        filterIco: notification.ico,
        fromNotification: true 
      } 
    });
  };

  const markAllAsRead = async () => {
    if (!currentUser?._id) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/notifications/read-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: currentUser._id }),
      });

      if (response.ok) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Chyba při označování všech notifikací jako přečtené:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'open': 'text-blue-600 dark:text-blue-400',
      'in-progress': 'text-yellow-600 dark:text-yellow-400',
      'waiting-preparation': 'text-orange-600 dark:text-orange-400',
      'contract-created': 'text-purple-600 dark:text-purple-400',
      'documents-sent': 'text-indigo-600 dark:text-indigo-400',
      'completed': 'text-green-600 dark:text-green-400',
      'rejected': 'text-red-600 dark:text-red-400',
      'needs-revision': 'text-amber-600 dark:text-amber-400',
      'orders-completed': 'text-emerald-600 dark:text-emerald-400',
    };
    return statusColors[status?.toLowerCase()] || 'text-gray-600 dark:text-gray-400';
  };

  const translateStatus = (status) => {
    const translations = {
      'open': 'Otevřený',
      'in-progress': 'Zpracovává se',
      'completed': 'Dokončený',
      'rejected': 'Zamítnutý',
      'needs-revision': 'Vráceno k doplnění',
      'waiting-preparation': 'Čeká na přípravu',
      'contract-created': 'Smlouva založena',
      'documents-sent': 'Dokumenty odeslány',
      'orders-completed': 'Objednávky dokončeny'
    };
    return translations[status] || status;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Před chvílí';
    if (diffMins < 60) return `Před ${diffMins} min`;
    if (diffHours < 24) return `Před ${diffHours} h`;
    if (diffDays < 7) return `Před ${diffDays} dny`;
    
    return date.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Zvoneček - fixní bublina dole vpravo (vedle chatu) - skryje se na mobilu když je dropdown otevřený */}
      {!(isMobile && isOpen) && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="fixed bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white p-3 rounded-full shadow-lg transition-all relative"
          style={{ 
            zIndex: 10000,
            position: 'fixed',
            bottom: isMobile ? 'calc(20px + env(safe-area-inset-bottom))' : '44px', // Níže (48px - 4px)
            right: isMobile ? '90px' : '112px' // Více doprava (120px - 8px)
          }}
          title="Notifikace"
        >
          <svg 
            className="w-6 h-6 text-white" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" 
            />
          </svg>
          
          {/* Badge s počtem nepřečtených */}
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Dropdown s notifikacemi - na mobilu celá obrazovka, na desktopu nad bublinou */}
      {isOpen && (
        <div 
          className={`fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 flex flex-col ${
            isMobile ? 'inset-0 rounded-none' : ''
          }`}
          style={{
            zIndex: 9999,
            position: 'fixed',
            ...(isMobile ? {
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              maxWidth: '100vw',
              maxHeight: '100vh',
              borderRadius: 0
            } : {
              bottom: '114px', // Nad bublinou (70px výška + mezera)
              right: '42px', // Zarovnáno s pravou stranou (stejně jako chat)
              width: '384px',
              maxWidth: 'calc(100vw - 40px)',
              maxHeight: '32rem'
            })
          }}
        >
          {/* Header */}
          <div 
            className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center"
            style={isMobile ? { paddingTop: 'calc(12px + env(safe-area-inset-top))' } : {}}
          >
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Notifikace ({unreadCount})
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  disabled={loading}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                >
                  {loading ? 'Načítání...' : 'Označit vše jako přečtené'}
                </button>
              )}
              {isMobile && (
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1"
                  title="Zavřít"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Seznam notifikací */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                <svg 
                  className="w-12 h-12 mx-auto mb-2 text-gray-400 dark:text-gray-600" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" 
                  />
                </svg>
                <p className="text-sm">Žádné nové notifikace</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {notifications.map((notification) => (
                  <div
                    key={notification._id}
                    className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors relative group"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="font-medium text-gray-900 dark:text-white text-sm pr-6">
                        {notification.companyName}
                      </h4>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification._id);
                        }}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Označit jako přečtené"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      IČO: {notification.ico}
                    </p>
                    
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                      {notification.requestTitle}
                    </p>
                    
                    {notification.type === 'lead_assignment' ? (
                      <div>
                        <p className="text-sm font-semibold text-orange-600 dark:text-orange-400 mb-0.5">🎯 Byl vám přidělen nový lead!</p>
                        {notification.batchName && (
                          <p className="text-xs text-gray-600 dark:text-gray-400">Šarže: <span className="font-medium">{notification.batchName}</span></p>
                        )}
                      </div>
                    ) : notification.type === 'new_comment' ? (
                      <div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          💬 Nový komentář od <span className="font-semibold">{notification.commentAuthor}</span>:
                        </p>
                        {notification.commentText && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 italic line-clamp-2">
                            „{notification.commentText}{notification.commentText.length >= 120 ? '…' : ''}"
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Změna stavu požadavku na:{' '}
                        <span className={`font-semibold ${getStatusColor(notification.newStatus)}`}>
                          {translateStatus(notification.newStatus)}
                        </span>
                      </p>
                    )}
                    
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                      {formatDate(notification.createdAt)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
